import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Base, Text, Icon } from '@brandwatch/axiom-components';
import { useSpring, config, animated } from 'react-spring';
import { generatePreventer } from '../../utils/documentScrollPreventer';
import { setSlideIndex } from '../../actions/slides';

import SlideLayout from '../../containers/SlideLayout';
import SlidePickerItem from './SlidePickerItem.jsx';

import { getFilteredSlides, getCurrentDeck } from '../../selectors';
import { useElementRect } from '../../hooks';

import './SlidePicker.css';

const mobileScreenMatch = () =>
  window.matchMedia('(max-device-width: 500px)').matches;

function getTextProps(hovered) {
  if (mobileScreenMatch()) {
    return {
      textSize: 'label',
      textStrong: true,
      textColor: hovered ? 'ui-accent' : 'subtle'
    };
  }
  return {
    textSize: 'large',
    textColor: hovered ? 'ui-accent' : 'body'
  };
}

const SlidePickerCounter = React.memo(({ search, length }) => {
  const textSize = mobileScreenMatch() ? 'headtitle' : 'headline';
  let counterText = `${length} slides`;
  if (search.searchText) {
    counterText = `(${length}) results for '${search.searchText}'`;
  }

  return (
    <div className="SlidePicker__counter">
      <Text textSize={textSize} textEllipsis>
        {counterText}
      </Text>
    </div>
  );
});

SlidePickerCounter.displayName = 'SlidePickerCounter';

SlidePickerCounter.propTypes = {
  search: PropTypes.object,
  length: PropTypes.number
};

const NoResults = ({ searchText }) => {
  return (
    <div className="NoResults__container">
      <div className="NoResults__Icon">
        <Icon name="magnify-glass" size="4rem" />
      </div>
      <Text textSize="headtitle" textCenter>
        We couldn&apos;t find any results for &apos;{searchText}&apos;
      </Text>
    </div>
  );
};

NoResults.propTypes = {
  searchText: PropTypes.string
};

const SlidePickerSlide = ({ gotoSlide, slide, slideOrder }) => {
  const [ref, rect] = useElementRect();
  const memoizedOnClick = useCallback(() => gotoSlide(slide.id), [slide]);
  const memoizedSlideItem = useCallback(
    props => (
      <SlidePickerItem onClick={memoizedOnClick} parentRect={rect} {...props} />
    ),
    [slide, ref.current]
  );

  const slideIndex = useMemo(() => slideOrder.findIndex(s => s === slide.id), [
    slideOrder,
    slide
  ]);

  const indexText = slideOrder.length
    ? `${slideIndex + 1}/${slideOrder.length}`
    : '';

  const [hovered, setHovered] = useState(false);
  const memoizedOnMouseEnter = useCallback(() => setHovered(true));
  const memoizedOnMouseLeave = useCallback(() => setHovered(false));

  const textProps = getTextProps(hovered);

  return (
    <Base
      className="SlidePicker__Slide"
      onMouseEnter={memoizedOnMouseEnter}
      onMouseLeave={memoizedOnMouseLeave}
    >
      <Base className="SlidePicker__SlideContent">
        <SlideLayout
          id={slide.id}
          Component={memoizedSlideItem}
          ref={ref}
          className="SlidePickerLayout"
        />
        <Base space="x3" className="SlidePicker__SlideText" data-viz-at="slide-picker-slide-name">
          <Text textEllipsis {...textProps}>
            {slide.name}
          </Text>
          <Text {...textProps}>{indexText}</Text>
        </Base>
      </Base>
    </Base>
  );
};

SlidePickerSlide.propTypes = {
  gotoSlide: PropTypes.func,
  slide: PropTypes.object.isRequired,
  slideOrder: PropTypes.array.isRequired
};

const preventDocumentScroll = generatePreventer('.SlidePicker__container');

const SlidePicker = React.memo(
  ({ setSlideIndex, slides, search, slideOrder, onRequestClose }) => {
    const memoizedgotoSlide = useCallback(slideId => {
      const slideIndex = slideOrder.findIndex(id => id === slideId);

      setSlideIndex(slideIndex, { slideReferrer: 'button' });
      onRequestClose();
    }, [setSlideIndex, slideOrder, onRequestClose]);

    useEffect(preventDocumentScroll);

    const [{ gradient }, set] = useSpring(() => ({
      gradient: 0,
      config: config.gentle,
      velocity: 10
    }));

    const scrollCallback = useCallback(e => {
      const st = e.target.scrollTop / 5;
      const grad = Math.min(st, 10);
      set({ gradient: grad });
    });

    const interp = g =>
      `linear-gradient(to bottom, rgba(0,0,0,1) -10%,rgba(0,0,0,0) ${g}%,rgba(0,0,0,0) 80%,rgba(0,0,0,1) 100%)`;

    const noResults = slideOrder.length && !slides.length;

    return (
      <React.Fragment>
        {!noResults && (
          <SlidePickerCounter length={slides.length} search={search} />
        )}
        <div className="SlidePicker__container" onScroll={scrollCallback}>
          <animated.div
            className="SlidePicker__gradient"
            style={{
              background: gradient.interpolate(interp)
            }}
          />
          {noResults && <NoResults searchText={search.searchText} />}
          {slides.map(slide => {
            return (
              <SlidePickerSlide
                key={slide.id}
                slide={slide}
                gotoSlide={memoizedgotoSlide}
                slideOrder={slideOrder}
              />
            );
          })}
        </div>
      </React.Fragment>
    );
  }
);

SlidePicker.propTypes = {
  setSlideIndex: PropTypes.func.isRequired,
  slides: PropTypes.array,
  slideOrder: PropTypes.array,
  onRequestClose: PropTypes.func,
  search: PropTypes.object
};

SlidePicker.defaultProps = {
  onRequestClose: () => {}
};

const mapStateToProps = state => ({
  slides: getFilteredSlides(state),
  slideOrder: getCurrentDeck(state).slide_order,
  search: state.search
});

const mapDispatchToProps = {
  setSlideIndex,
};

export default connect(mapStateToProps, mapDispatchToProps)(SlidePicker);
